/*
  CombinedDraftDialog — Stage 4 of the Handwriting Recognition spec.

  Merges the current handwriting upload with (optionally) a recent
  SOAP session and/or an intake submission for the same client into
  a single attributed assessment draft. Shows source-tagged bullets
  and any cross-source conflicts the LLM flagged for practitioner
  review.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Sparkles, Loader2, AlertTriangle, Mic, FileText, ScanLine,
} from "lucide-react";
import AIProvenanceBadge from "@/components/AIProvenanceBadge";
import AiSourcesToggle from "@/components/AiSourcesToggle";
import { cn } from "@/lib/utils";


const SOURCE_TONE = {
  handwriting: "bg-indigo-50 text-indigo-800 border-indigo-300",
  audio:       "bg-purple-50 text-purple-800 border-purple-300",
  intake:      "bg-sky-50 text-sky-800 border-sky-300",
};


export default function CombinedDraftDialog({
  uploadId, clientEmail, open, onOpenChange, onDrafted,
}) {
  const [audioSessions, setAudioSessions] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedIntakeId, setSelectedIntakeId] = useState("");
  const [loadingLists, setLoadingLists] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [result, setResult] = useState(null);

  const loadCandidates = useCallback(async () => {
    if (!clientEmail) return;
    setLoadingLists(true);
    try {
      const enc = encodeURIComponent(clientEmail);
      // Pull a few recent audio sessions + intake submissions.
      const [sess, intk] = await Promise.all([
        api.get(`/soap-notes/sessions?client_email=${enc}&limit=10`).catch(() => ({ data: { sessions: [] } })),
        api.get(`/intake/clients/by-email/${enc}/submissions`).catch(() => ({ data: { submissions: [] } })),
      ]);
      setAudioSessions((sess.data?.sessions || []).filter((s) => s.transcript || s.status === "done"));
      setIntakes(intk.data?.submissions || []);
    } catch (e) {
      toast.error("Couldn't load source candidates", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoadingLists(false);
    }
  }, [clientEmail]);

  useEffect(() => { if (open) { loadCandidates(); setResult(null); } }, [open, loadCandidates]);

  async function draft() {
    setDrafting(true);
    try {
      const r = await api.post("/handwriting/combined-drafts", {
        handwriting_upload_ids: uploadId ? [uploadId] : [],
        soap_session_id: selectedSessionId || null,
        intake_submission_id: selectedIntakeId || null,
      });
      setResult(r.data);
      toast.success("Combined draft created", {
        description: `${r.data.conflicts?.length || 0} conflict(s) flagged for review`,
      });
      onDrafted?.(r.data);
    } catch (e) {
      toast.error("Draft failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setDrafting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[92vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="combined-draft-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-700" />
            Combined AI assessment draft
            <AIProvenanceBadge testId="combined-draft-provenance-badge"
                                confidence={result?.confidence}
                                source="handwriting + selected sources" />
          </DialogTitle>
          <DialogDescription>
            Pick which other sources to merge with this handwriting document. Every bullet in the
            resulting SOAP draft is source-tagged; disagreements between sources are flagged for
            practitioner review. The draft is saved as <span className="font-medium">draft</span>—
            AI never finalises a note.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-4">
          {!result ? (
            <>
              <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3">
                <div className="text-xs font-medium text-indigo-900 flex items-center gap-2">
                  <ScanLine className="h-3.5 w-3.5" /> Handwriting upload
                </div>
                <div className="text-xs text-zinc-700 mt-1 font-mono-num">
                  {uploadId ? `#${uploadId.slice(0, 8)}…` : "—"}
                </div>
              </div>

              <div className="border border-zinc-200 rounded-md p-3">
                <Label className="text-xs flex items-center gap-1"><Mic className="h-3.5 w-3.5" /> Audio session (optional)</Label>
                {loadingLists ? (
                  <div className="text-xs text-zinc-500 py-2"><Loader2 className="h-3.5 w-3.5 inline mr-1 animate-spin" /> Loading…</div>
                ) : audioSessions.length === 0 ? (
                  <div className="text-xs text-zinc-500 py-2">No completed recorded sessions on file.</div>
                ) : (
                  <div className="space-y-1 mt-2">
                    {audioSessions.slice(0, 5).map((s) => (
                      <label key={s.id} className="flex items-start gap-2 text-xs cursor-pointer">
                        <input
                          type="radio"
                          name="audio-source"
                          checked={selectedSessionId === s.id}
                          onChange={() => setSelectedSessionId(s.id === selectedSessionId ? "" : s.id)}
                          data-testid={`combined-audio-${s.id}`}
                        />
                        <div className="flex-1">
                          <div className="text-zinc-900">{s.appointment_type || "Session"}</div>
                          <div className="text-zinc-500">{s.finalized_at ? new Date(s.finalized_at).toLocaleString() : s.status}</div>
                        </div>
                      </label>
                    ))}
                    {selectedSessionId && (
                      <Button size="sm" variant="ghost" onClick={() => setSelectedSessionId("")}>Clear</Button>
                    )}
                  </div>
                )}
              </div>

              <div className="border border-zinc-200 rounded-md p-3">
                <Label className="text-xs flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Intake submission (optional)</Label>
                {loadingLists ? (
                  <div className="text-xs text-zinc-500 py-2"><Loader2 className="h-3.5 w-3.5 inline mr-1 animate-spin" /> Loading…</div>
                ) : intakes.length === 0 ? (
                  <div className="text-xs text-zinc-500 py-2">No intake submissions on file.</div>
                ) : (
                  <div className="space-y-1 mt-2">
                    {intakes.slice(0, 5).map((s) => (
                      <label key={s.id || s._id} className="flex items-start gap-2 text-xs cursor-pointer">
                        <input
                          type="radio"
                          name="intake-source"
                          checked={selectedIntakeId === (s.id || s._id)}
                          onChange={() => setSelectedIntakeId((s.id || s._id) === selectedIntakeId ? "" : (s.id || s._id))}
                          data-testid={`combined-intake-${s.id || s._id}`}
                        />
                        <div className="flex-1">
                          <div className="text-zinc-900">{s.template_name || s.template_key || "Intake"}</div>
                          <div className="text-zinc-500">{s.submitted_at ? new Date(s.submitted_at).toLocaleString() : ""}</div>
                        </div>
                      </label>
                    ))}
                    {selectedIntakeId && (
                      <Button size="sm" variant="ghost" onClick={() => setSelectedIntakeId("")}>Clear</Button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {result.conflicts?.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                  <div className="text-xs font-semibold text-amber-900 flex items-center gap-1 mb-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> {result.conflicts.length} cross-source conflict(s) — practitioner review required
                  </div>
                  <ul className="text-xs text-amber-900 space-y-2 mt-2">
                    {result.conflicts.map((c, i) => (
                      <li key={i} className="border border-amber-200 rounded bg-white p-2" data-testid={`combined-conflict-${i}`}>
                        <div className="font-medium">{c.topic}</div>
                        <ul className="mt-1 space-y-0.5">
                          {(c.details || []).map((d, j) => (
                            <li key={j}>
                              <Badge className={cn("text-2xs uppercase border mr-1.5", SOURCE_TONE[d.source] || "")}>
                                {d.source}
                              </Badge>
                              <span className="text-zinc-800">{d.says}</span>
                            </li>
                          ))}
                        </ul>
                        {c.note && <div className="text-2xs text-amber-800 italic mt-1">{c.note}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {["subjective", "objective", "assessment", "plan"].map((section) => {
                const items = result.attribution?.[section] || [];
                if (items.length === 0) return null;
                return (
                  <section key={section} data-testid={`combined-section-${section}`}>
                    <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1">
                      {section}
                    </div>
                    <ul className="space-y-1">
                      {items.map((it, i) => (
                        <li key={i} className="text-sm text-zinc-900 flex items-start gap-2">
                          <span className="text-zinc-400 mt-0.5">·</span>
                          <div className="flex-1">
                            <div>{it.text}</div>
                            <div className="mt-0.5 flex gap-1 flex-wrap">
                              {(it.sources || []).map((s) => (
                                <Badge key={s} className={cn("text-2xs uppercase border", SOURCE_TONE[s] || "")}>
                                  {s}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}

              <div className="text-2xs text-zinc-500 border-t border-zinc-200 pt-2">
                Draft SOAP note <span className="font-mono">#{result.soap_note_id.slice(0, 8)}…</span> saved as <span className="font-medium">draft</span>. Open it under the client&apos;s Notes / Forms tab to review and finalise.
              </div>
              <AiSourcesToggle
                sources={result.sources_used}
                testId="combined-sources-toggle"
              />
            </>
          )}
        </div>

        <DialogFooter className="border-t pt-3 sm:justify-end gap-2">
          {!result && (
            <Button
              onClick={draft}
              disabled={drafting || !uploadId}
              data-testid="combined-generate-btn"
            >
              {drafting
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Generating…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Generate combined draft</>
              }
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {result ? "Done" : "Cancel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
